import { useState } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Tag } from '@/components/ui/Tag';
import { TextField } from '@/components/forms/TextField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';
import { fetchActivePartnerCategories } from './partner-categories.api';
import type { PartnerProfileFormValues } from './schema';

interface ServicesSectionProps {
  control: Control<PartnerProfileFormValues>;
  register: UseFormRegister<PartnerProfileFormValues>;
  errors?: FieldErrors<PartnerProfileFormValues>;
  /** Resolves category ids to display names for the selected chips. */
  categoryNameById: Record<string, string>;
}

/** Free-form "Services Offered" tag entry: type a value, press Enter to add. */
function ServicesOfferedInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const addTag = () => {
    const trimmed = draft.trim();
    if (trimmed && !value.includes(trimmed)) onChange([...value, trimmed]);
    setDraft('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-input border border-input-border p-2">
      {value.map((tag) => (
        <Tag key={tag} onRemove={() => onChange(value.filter((t) => t !== tag))}>
          {tag}
        </Tag>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addTag();
          }
        }}
        placeholder={S.SERVICES_OFFERED_PLACEHOLDER}
        className="min-w-[160px] flex-1 border-none bg-transparent p-1 text-p6 text-navy outline-none placeholder:text-slate"
      />
    </div>
  );
}

/**
 * The Services & categories panel: category picker, free-form service tags
 * and tagline (FR-28). The read view is `PartnerServicesCard` (MYN-217).
 */
export function ServicesSection({ control, register, errors, categoryNameById }: ServicesSectionProps) {
  const categoriesQuery = useQuery({
    queryKey: ['partner-categories', 'active'],
    queryFn: fetchActivePartnerCategories,
  });
  const categories = categoriesQuery.data ?? [];

  return (
    <Card title={S.SERVICES} headerDivider hoverEffect={false}>
      <div className="flex flex-col gap-5">
        <Controller
          control={control}
          name="categoryIds"
          render={({ field, fieldState }) => {
            const selected = field.value ?? [];
            const available = categories.filter((c) => !selected.includes(c.id));
            return (
              <div>
                <FieldLabel label={S.SERVICE_CATEGORIES} variant="caps" required />
                <div className="mt-2 flex flex-wrap gap-2">
                  {selected.map((id) => (
                    <Tag key={id} onRemove={() => field.onChange(selected.filter((c) => c !== id))}>
                      {categoryNameById[id] ?? id}
                    </Tag>
                  ))}
                  {available.map((category) => (
                    <Tag key={category.id} onClick={() => field.onChange([...selected, category.id])}>
                      {category.name}
                    </Tag>
                  ))}
                </div>
                {fieldState.error && <p className="mt-1 text-xs text-danger">{fieldState.error.message}</p>}
              </div>
            );
          }}
        />

        <Controller
          control={control}
          name="tags"
          render={({ field }) => (
            <div>
              <FieldLabel label={S.SERVICES_OFFERED} variant="caps" optional />
              <div className="mt-2">
                <ServicesOfferedInput value={field.value ?? []} onChange={field.onChange} />
              </div>
            </div>
          )}
        />

        <TextField
          {...register('tagline')}
          label={S.TAGLINE}
          labelVariant="caps"
          maxLength={S.TAGLINE_MAX_LENGTH}
          error={errors?.tagline?.message}
        />
      </div>
    </Card>
  );
}
